// ============================================
// AVISOSINCRONIZACIONSYSCOM.JS — "el catalogo lleva N dias sin actualizarse"
// ============================================
// SYSCOM-REIMPORT (2026-09-21). Encargo de Ricardo, con su motivo dentro:
//
//   "el barrido llevaba fallando desde el 28 de agosto y nadie lo supo hasta que busque
//    un producto a mano. Quiero que si la importacion o el refresco de precios falla, se
//    vea. Un fallo silencioso de tres meses no puede repetirse."
//
// El dato SIEMPRE estuvo en la base (estado='error', mensaje 403 de la categoria 22).
// Lo que no existia era un sitio donde mirarlo sin ir a buscarlo. Este aviso es ese
// sitio, y por eso vive en las DOS pantallas donde direccion entra sin proponerselo:
// Mi dia (lo primero que se abre) y Conexion Syscom (donde se va a arreglar).
//
// SE OCULTA SOLO cuando no hay nada que decir y cuando quien mira no es direccion: el
// backend responde 401/403 a cualquier otro y el componente no pinta nada. No hay que
// filtrarlo por rol aqui; el candado esta en el servidor (routes/syscom.js).

import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../config/api';
import { authHeaders } from '../utils/storage';

const PP = "'Poppins', sans-serif";

// Rojo = algo esta roto ahora mismo. Ambar = termino, pero incompleto o hace mucho.
const TONO = {
  error: { fondo: '#fdecec', borde: '#f3c9c9', texto: '#8a2b2b', icono: '🔴' },
  aviso: { fondo: '#fff8ed', borde: '#fce5b8', texto: '#8a5a1b', icono: '🟡' },
};

const fechaCorta = (v) => {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d) ? null : d.toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
};

export default function AvisoSincronizacionSyscom({ onNavegar = null, estilo = {} }) {
  const [avisos, setAvisos] = useState([]);
  const [oculto, setOculto] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/syscom/salud-sincronizacion`, { headers: authHeaders() });
      // 401/403 = quien mira no es direccion. 5xx = la propia consulta fallo. En los dos
      // casos el aviso calla: no vale la pena asustar con un error del termometro.
      if (!resp.ok) return;
      const r = await resp.json();
      if (r.ok && Array.isArray(r.avisos)) setAvisos(r.avisos);
    } catch { /* sin red: el aviso simplemente no aparece */ }
  }, []);

  useEffect(() => {
    cargar();
    // Se vuelve a mirar cada 10 minutos por si la pantalla se queda abierta todo el dia.
    const id = setInterval(cargar, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [cargar]);

  if (oculto || !avisos.length) return null;

  const hayError = avisos.some(a => a.nivel === 'error');
  const tono = hayError ? TONO.error : TONO.aviso;

  const marcarVisto = async () => {
    setOculto(true); // se quita ya: si la llamada falla, vuelve al recargar la pantalla
    try {
      await fetch(`${API_BASE}/api/syscom/salud-sincronizacion/visto`, {
        method: 'POST', headers: authHeaders(),
      });
    } catch { /* best-effort */ }
  };

  const boton = {
    padding: '8px 14px', minHeight: 40, borderRadius: 8, cursor: 'pointer',
    fontFamily: PP, fontSize: 13, fontWeight: 600, border: `1px solid ${tono.borde}`,
    background: '#fff', color: tono.texto,
  };

  return (
    <div
      style={{
        background: tono.fondo, border: `1px solid ${tono.borde}`, borderRadius: 14,
        padding: 16, fontFamily: PP, color: tono.texto, ...estilo,
      }}
    >
      <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 10 }}>
        {tono.icono} El catálogo de Syscom necesita una mirada
      </div>

      {avisos.map((a, i) => (
        <div
          key={i}
          style={{
            background: '#fff', borderRadius: 10, padding: '10px 12px',
            borderLeft: `5px solid ${a.nivel === 'error' ? '#c0392b' : '#d79a2b'}`,
            marginBottom: 8,
          }}
        >
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#2e3338' }}>
            {a.titulo}
            {fechaCorta(a.cuando) && (
              <span style={{ fontWeight: 500, color: '#7a8189' }}> · {fechaCorta(a.cuando)}</span>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: '#5e666e', marginTop: 4, wordBreak: 'break-word' }}>
            {a.detalle}
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        {onNavegar && (
          <button
            style={{ ...boton, background: tono.texto, color: '#fff', borderColor: tono.texto }}
            onClick={() => onNavegar('cotizaciones.syscom')}
          >
            Ir a Conexión Syscom
          </button>
        )}
        <button style={boton} onClick={marcarVisto}>Entendido</button>
      </div>
    </div>
  );
}
